if (!customElements.get('photo-upload')) {
  // 1600px is plenty for the bracelet print and keeps the photo around 200-400 KB.
  const MAX_SIDE = 1600;
  const JPEG_QUALITY = 0.85;
  const MAX_BYTES = 20 * 1024 * 1024;
  const UPLOAD_TIMEOUT = 30000;

  customElements.define(
    'photo-upload',
    class PhotoUpload extends HTMLElement {
      connectedCallback() {
        this.value = this.querySelector('.photo-upload__value');
        this.file = this.querySelector('.photo-upload__file');
        this.pick = this.querySelector('.photo-upload__pick');
        this.status = this.querySelector('.photo-upload__status');
        this.thumb = this.querySelector('.photo-upload__thumb');
        this.label = this.querySelector('.photo-upload__label-text');
        this.bar = this.querySelector('.photo-upload__bar span');
        this.error = this.querySelector('.photo-upload__error');
        this.uploadId = 0;

        const choose = () => this.file.click();
        this.pick.addEventListener('click', choose);
        this.querySelector('.photo-upload__change').addEventListener('click', choose);
        this.file.addEventListener('change', () => {
          const file = this.file.files[0];
          // Reset the picker so choosing the same photo again still triggers "change".
          this.file.value = '';
          if (file) this.upload(file);
        });
        this.thumb.addEventListener('error', () => (this.thumb.style.visibility = 'hidden'));
      }

      get uploading() {
        return this.classList.contains('is-uploading');
      }

      get ready() {
        return !this.value.disabled && this.value.value !== '';
      }

      async upload(file) {
        const uploadId = ++this.uploadId;
        const mimeType = file.type || (/\.(heic|heif)$/i.test(file.name) ? 'image/heic' : '');
        if (!mimeType.startsWith('image/')) {
          this.showError('Ce fichier n’est pas une image. Choisissez une photo.');
          return;
        }

        this.value.value = '';
        this.value.disabled = true;
        this.hideError();
        this.setState('is-uploading', 'Envoi de la photo…');
        this.startProgress();
        if (this.thumb.src.startsWith('blob:')) URL.revokeObjectURL(this.thumb.src);
        this.thumb.style.visibility = '';
        this.thumb.src = URL.createObjectURL(file);

        try {
          // Let the "uploading" state paint first. setTimeout, not requestAnimationFrame:
          // it also fires when the tab is in the background.
          await new Promise((resolve) => setTimeout(resolve, 30));
          const photo = await this.compress(file);
          if (photo.size > MAX_BYTES) throw new Error('size');
          this.setProgress(0);
          const result = await this.send(photo, uploadId).catch((error) => {
            if (error.message === 'size' || uploadId !== this.uploadId) throw error;
            return this.send(photo, uploadId);
          });
          if (uploadId !== this.uploadId) return;
          await this.finishProgress();
          if (uploadId !== this.uploadId) return;
          this.value.value = this.dataset.viewerUrl + result.public_id;
          this.value.disabled = false;
          this.setState('is-done', this.dataset.doneLabel);
          this.hideError();
          if (this.pendingSubmit) {
            const button = this.pendingSubmit.querySelector('[type="submit"][name="add"]');
            this.setPending(null);
            if (button) button.click();
          }
        } catch (error) {
          if (uploadId !== this.uploadId) return;
          this.setPending(null);
          this.setState('', '');
          this.showError(
            error.message === 'size'
              ? 'Photo trop lourde (20 Mo maximum). Choisissez-en une autre.'
              : 'L’envoi de la photo a échoué. Vérifiez votre connexion et réessayez.'
          );
        }
      }

      // XMLHttpRequest rather than fetch, for the upload progress bar.
      send(photo, uploadId) {
        return new Promise((resolve, reject) => {
          const data = new FormData();
          data.append('file', photo);
          data.append('upload_preset', this.dataset.uploadPreset);
          const request = new XMLHttpRequest();
          request.open('POST', `https://api.cloudinary.com/v1_1/${this.dataset.cloudName}/image/upload`);
          request.timeout = UPLOAD_TIMEOUT;
          request.upload.onprogress = (event) => {
            if (event.lengthComputable && uploadId === this.uploadId) this.setProgress(event.loaded / event.total);
          };
          request.upload.onload = () => {
            if (uploadId === this.uploadId) this.setProgress(1);
          };
          request.onload = () => {
            let result = {};
            try {
              result = JSON.parse(request.responseText);
            } catch (error) {}
            if (request.status === 200 && result.public_id) resolve(result);
            else reject(new Error(/size/i.test(result.error?.message || '') ? 'size' : 'upload'));
          };
          request.onerror = request.ontimeout = () => reject(new Error('upload'));
          request.send(data);
        });
      }

      async compress(file) {
        try {
          const source = await this.decode(file);
          const scale = Math.min(1, MAX_SIDE / Math.max(source.width, source.height));
          const width = Math.round(source.width * scale);
          const height = Math.round(source.height * scale);
          // Resizing inside createImageBitmap happens off the main thread where supported.
          const image =
            scale < 1 && source instanceof ImageBitmap
              ? await createImageBitmap(source, { resizeWidth: width, resizeHeight: height, resizeQuality: 'high' }).catch(() => source)
              : source;
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const context = canvas.getContext('2d');
          context.fillStyle = '#fff';
          context.fillRect(0, 0, width, height);
          context.drawImage(image, 0, 0, width, height);
          const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
          if (!blob) return file;
          const keepOriginal = scale === 1 && file.type === 'image/jpeg' && file.size <= blob.size;
          return keepOriginal ? file : new File([blob], 'photo.jpg', { type: 'image/jpeg' });
        } catch (error) {
          // Format the browser cannot decode (e.g. HEIC on a computer): Cloudinary converts it to JPEG.
          return file;
        }
      }

      async decode(file) {
        if (window.createImageBitmap) {
          try {
            return await createImageBitmap(file, { imageOrientation: 'from-image' });
          } catch (error) {}
        }
        const url = URL.createObjectURL(file);
        try {
          const image = new Image();
          image.src = url;
          await image.decode();
          // Not attached to the page, so width/height are the natural dimensions.
          return image;
        } finally {
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
      }

      // While waiting for the upload, dim "Ajouter au panier" (Atelier has no spinner on that button).
    setPending(form) {
      const previous = this.pendingSubmit && this.pendingSubmit.querySelector('[type="submit"][name="add"]');
      if (previous) {
        previous.classList.remove('bp-photo-pending');
        previous.removeAttribute('aria-busy');
      }
      this.pendingSubmit = form;
      const button = form && form.querySelector('[type="submit"][name="add"]');
      if (button) {
        button.classList.add('bp-photo-pending');
        button.setAttribute('aria-busy', 'true');
      }
    }

    // Smooth progress bar in three steps: compression 0-20% (CSS animation, keeps moving while
      // the page is busy), sending 20-80% (real upload progress), Cloudinary processing 80-97%.
      // The bar keeps creeping towards the ceiling of the current step so it never freezes or jumps.
      startProgress() {
        cancelAnimationFrame(this.progressFrame);
        this.bar.style.transform = '';
        this.classList.remove('is-compressing');
        void this.bar.offsetWidth; // restart the CSS animation
        this.classList.add('is-compressing');
        this.progressShown = 0;
        this.progressTarget = 0;
        this.progressCeiling = 0.2;
      }

      setProgress(ratio) {
        if (this.classList.contains('is-compressing')) {
          // Take over from the CSS animation exactly where it is.
          const matrix = getComputedStyle(this.bar).transform;
          this.progressShown = matrix.startsWith('matrix(') ? parseFloat(matrix.slice(7)) : 0.2;
          this.progressTarget = this.progressShown;
          this.classList.remove('is-compressing');
          const tick = () => {
            this.progressTarget += (this.progressCeiling - this.progressTarget) * 0.008;
            this.progressShown += (this.progressTarget - this.progressShown) * 0.12;
            this.bar.style.transform = `scaleX(${this.progressShown.toFixed(4)})`;
            this.progressFrame = requestAnimationFrame(tick);
          };
          tick();
        }
        this.progressCeiling = ratio >= 1 ? 0.97 : 0.8;
        this.progressTarget = Math.max(this.progressTarget, 0.2 + ratio * 0.6);
      }

      finishProgress() {
        cancelAnimationFrame(this.progressFrame);
        this.classList.remove('is-compressing');
        this.bar.style.transition = 'transform 0.2s ease-out';
        this.bar.style.transform = 'scaleX(1)';
        return new Promise((resolve) =>
          setTimeout(() => {
            this.bar.style.transition = '';
            resolve();
          }, 250)
        );
      }

      setState(state, text) {
        if (state !== 'is-uploading') {
          cancelAnimationFrame(this.progressFrame);
          this.classList.remove('is-compressing');
        }
        this.classList.remove('is-uploading', 'is-done');
        if (state) this.classList.add(state);
        this.status.hidden = !state;
        this.pick.hidden = Boolean(state);
        this.label.textContent = text;
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = false;
      }

      hideError() {
        this.error.hidden = true;
      }
    }
  );

  // Block "Ajouter au panier" until the photo is uploaded. Listens on window in the capture phase,
  // so it runs before Atelier's own listeners (document, capture phase): no "added" animation and
  // no cart request without the photo. The sticky add-to-cart bar clicks the same button.
  const guard = (event) => {
    let form = event.target;
    if (event.type === 'click') {
      const button = event.target.closest && event.target.closest('button[type="submit"][name="add"]');
      if (!button) return;
      form = button.form;
    }
    // getAttribute: form.id would return the variant <input name="id"> inside the form.
    const formId = form && form.getAttribute && form.getAttribute('id');
    if (!formId) return;
    const upload = document.querySelector(`photo-upload[data-form-id="${CSS.escape(formId)}"]`);
    if (!upload || upload.ready) return;
    if (!upload.uploading && upload.dataset.required === 'false') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (upload.uploading) {
      // Add to cart automatically as soon as the upload finishes.
      upload.setPending(form);
      return;
    }
    upload.showError(upload.dataset.requiredMessage);
    upload.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  window.addEventListener('click', guard, true);
  window.addEventListener('submit', guard, true);
}
